import { useState, useEffect, useCallback } from 'react';
import { SignedIn, useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';
import ItemForm from '../components/ItemForm';

export default function MyItems() {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [carouselIndices, setCarouselIndices] = useState({}); // Track current image index per item
  const [showAddForm, setShowAddForm] = useState(false); // true = show Add Item modal
  const [editingItem, setEditingItem] = useState(null); // Item being edited (null = not editing)
  const [deletingItemId, setDeletingItemId] = useState(null); // Item ID being deleted (null = not deleting)
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api('/api/items/my-items', { getToken });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Error ${res.status}`);
      }
      const data = await res.json();
      setItems(data);
      // Initialize carousel indices
      const indices = {};
      data.forEach(item => {
        indices[item.id] = 0;
      });
      setCarouselIndices(indices);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleCarouselNext = (itemId, maxIndex) => {
    setCarouselIndices(prev => ({
      ...prev,
      [itemId]: prev[itemId] < maxIndex ? prev[itemId] + 1 : 0
    }));
  };

  const handleCarouselPrev = (itemId, maxIndex) => {
    setCarouselIndices(prev => ({
      ...prev,
      [itemId]: prev[itemId] > 0 ? prev[itemId] - 1 : maxIndex
    }));
  };

  const handleDownloadQr = async (item) => {
    if (!item.qrCodeUrl) {
      alert('QR code not available for this item');
      return;
    }
    
    try {
      // Fetch the QR code image
      const response = await fetch(item.qrCodeUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `item-${item.id}-qr.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading QR code:', err);
      alert('Failed to download QR code');
    }
  };

  const handleFacebookShare = async (item) => {
    if (!item.facebookPrefillText) {
      alert('Facebook share text not available for this item');
      return;
    }

    try {
      // Copy item details to clipboard
      await navigator.clipboard.writeText(item.facebookPrefillText);
      
      // Open Facebook Marketplace create page
      window.open('https://www.facebook.com/marketplace/create/item', '_blank');
      
      // Show a brief message (you could use a toast notification here instead)
      alert('Details copied! Paste into Facebook Marketplace.');
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      // Fallback: just open Facebook Marketplace
      window.open('https://www.facebook.com/marketplace/create/item', '_blank');
      alert('Please copy the item details manually and paste into Facebook Marketplace.');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
  };

  const handleEditCancel = () => {
    setEditingItem(null);
  };

  const handleAddSuccess = () => {
    setShowAddForm(false);
    fetchItems();
  };

  const handleEditSuccess = () => {
    setEditingItem(null);
    fetchItems(); // Refresh the list
  };

  const handleDeleteClick = (itemId) => {
    setDeletingItemId(itemId);
  };

  const handleDeleteCancel = () => {
    setDeletingItemId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingItemId) return;

    setDeleteLoading(true);
    try {
      const res = await api(`/api/items/${deletingItemId}`, {
        getToken,
        method: 'DELETE',
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Error ${res.status}`);
      }

      // Close dialog and refresh list
      setDeletingItemId(null);
      fetchItems(); // Refresh the list
    } catch (err) {
      alert(`Failed to delete item: ${err.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Filter items by search (title and description), only when we have items
  const searchLower = (searchQuery || '').trim().toLowerCase();
  const filteredItems = searchLower
    ? items.filter(
        (item) =>
          (item.title || '').toLowerCase().includes(searchLower) ||
          (item.description || '').toLowerCase().includes(searchLower)
      )
    : items;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-2xl text-slate-500">Loading items…</p>
      </div>
    );
  }

  return (
    <SignedIn>
      <div className="flex flex-col gap-6">
        <h1 className="text-3xl font-semibold text-center">My Items</h1>

        {error && (
          <p className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">
            {error}
          </p>
        )}

        {/* Search bar — filters vendor's items only */}
        {items.length > 0 && (
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden>
              🔍
            </span>
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search your items…"
              className="w-full h-12 pl-12 pr-4 rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition-colors"
              aria-label="Search your items"
            />
            {searchQuery.trim() && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300 flex items-center justify-center hover:bg-slate-300 dark:hover:bg-slate-500 transition-colors"
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
        )}

        {/* Add item button — always visible */}
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="h-16 text-2xl w-full rounded-xl font-semibold bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add item
        </button>

        {items.length === 0 ? (
          <div className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 p-8 text-center">
            <p className="text-xl text-slate-600 dark:text-slate-400">
              You haven't created any items yet.
            </p>
            <p className="text-lg text-slate-500 dark:text-slate-500 mt-2">
              Use the button above to list your first item.
            </p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 p-8 text-center">
            <p className="text-xl text-slate-600 dark:text-slate-400">
              No items match &quot;{searchQuery.trim()}&quot;.
            </p>
            <p className="text-lg text-slate-500 dark:text-slate-500 mt-2">
              Try a different search or clear the search to see all items.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {filteredItems.map((item) => {
              const currentImageIndex = carouselIndices[item.id] || 0;
              const imageUrls = item.imageUrls || [];
              const hasLowStock = item.stockCount < 3;
              const isOutOfStock = item.stockCount <= 0;

              return (
                <div
                  key={item.id}
                  className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 overflow-hidden"
                >
                  {/* Image Carousel */}
                  <div className="relative w-full aspect-square bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
                    {imageUrls.length > 0 ? (
                      <>
                        <img
                          src={imageUrls[currentImageIndex]}
                          alt={item.title}
                          className="w-full h-full object-contain"
                        />
                        {imageUrls.length > 1 && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleCarouselPrev(item.id, imageUrls.length - 1)}
                              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                              aria-label="Previous image"
                            >
                              ‹
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCarouselNext(item.id, imageUrls.length - 1)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                              aria-label="Next image"
                            >
                              ›
                            </button>
                            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                              {imageUrls.map((_, idx) => (
                                <div
                                  key={idx}
                                  className={`w-2 h-2 rounded-full ${
                                    idx === currentImageIndex
                                      ? 'bg-white'
                                      : 'bg-white/50'
                                  }`}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                        No image
                      </div>
                    )}
                  </div>

                  {/* Card Content */}
                  <div className="p-4 flex flex-col gap-3">
                    {/* Title */}
                    <h2 className="text-xl font-semibold line-clamp-2">{item.title}</h2>

                    {/* Price and Stock */}
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                        ${parseFloat(item.price).toFixed(2)}
                      </span>
                      <div className="flex items-center gap-2">
                        {isOutOfStock ? (
                          <span className="px-3 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-sm font-medium">
                            Out of Stock
                          </span>
                        ) : hasLowStock ? (
                          <span className="px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-sm font-medium">
                            Low Stock ({item.stockCount})
                          </span>
                        ) : (
                          <span className="px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-sm font-medium">
                            In Stock ({item.stockCount})
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    {item.description && (
                      <p className="text-slate-600 dark:text-slate-400 line-clamp-2">
                        {item.description}
                      </p>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2 mt-2">
                      {/* QR Download and Facebook Share */}
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => handleDownloadQr(item)}
                          disabled={!item.qrCodeUrl}
                          className="h-14 text-lg w-full rounded-xl font-semibold bg-slate-600 text-white hover:bg-slate-700 active:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                          </svg>
                          Download QR
                        </button>
                        <button
                          type="button"
                          onClick={() => handleFacebookShare(item)}
                          disabled={!item.facebookPrefillText}
                          className="h-14 text-lg w-full rounded-xl font-semibold bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                        >
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                          Share on Facebook
                        </button>
                      </div>

                      {/* Edit and Delete */}
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(item)}
                          className="h-12 text-base w-full rounded-xl font-medium bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(item.id)}
                          className="h-12 text-base w-full rounded-xl font-medium bg-red-600 text-white hover:bg-red-700 active:bg-red-800 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add Item Modal */}
        {showAddForm && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={() => setShowAddForm(false)}
          >
            <div
              className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-semibold mb-4">Add new item</h2>
              <ItemForm
                onSuccess={handleAddSuccess}
                onCancel={() => setShowAddForm(false)}
              />
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editingItem && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={handleEditCancel}
          >
            <div 
              className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <ItemForm
                item={editingItem}
                onSuccess={handleEditSuccess}
                onCancel={handleEditCancel}
              />
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {deletingItemId && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={handleDeleteCancel}
          >
            <div 
              className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-semibold mb-4">Delete Item</h2>
              <p className="text-lg text-slate-600 dark:text-slate-400 mb-6">
                Are you sure you want to delete this item? This action cannot be undone.
              </p>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={handleDeleteCancel}
                  disabled={deleteLoading}
                  className="flex-1 h-12 text-base rounded-xl font-medium bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={deleteLoading}
                  className="flex-1 h-12 text-base rounded-xl font-medium bg-red-600 text-white hover:bg-red-700 active:bg-red-800 disabled:opacity-50 transition-colors"
                >
                  {deleteLoading ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SignedIn>
  );
}
